import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LayoutGrid, MessageSquare, Search, ShoppingCart, Store } from "lucide-react";
import { ButtonLink, Logo, Skeleton } from "@/components/ui";
import { MegaMenu } from "@/components/search/MegaMenu";
import { CategorySheet } from "@/components/search/CategorySheet";
import { CartDrawer } from "@/features/cart/CartDrawer";
import { useCartCount } from "@/features/cart/api";
import { useChatsUnread } from "@/features/chats/api";
import { NotificationBell } from "@/features/notifications/NotificationBell";
import { SearchCommand } from "@/components/search/SearchCommand";
import { useSearchShortcut } from "@/components/search/useSearchShortcut";
import { cn } from "@/lib/cn";
import { useAuth } from "@/stores/auth";
import { useUI } from "@/stores/ui";
import { useAppShell } from "@/lib/useAppShell";
import { MobileHomeBar, MobileInnerBar, useHomeCollapsed } from "./MobileTopBar";
import { UserMenu } from "./UserMenu";
import { CategoryNavRow, DesktopMainBar, UtilityBar } from "./DesktopHeader";

/**
 * Sticky glass top bar. On phones it works like a shopping app: the glass reaches up behind the
 * status bar / Dynamic Island while the content starts below it, with a logo-and-icons row and a
 * full-width search row underneath. Gains a glow border once the page scrolls.
 */
export function GlassNavbar() {
  const status = useAuth((s) => s.status);
  const isVendor = useAuth((s) => !!s.user?.is_vendor);
  const searchOpen = useUI((s) => s.searchOpen);
  const setSearchOpen = useUI((s) => s.setSearchOpen);
  const openSearch = useCallback(() => setSearchOpen(true), [setSearchOpen]);
  const setCartOpen = useUI((s) => s.setCartOpen);
  const setCategoriesOpen = useUI((s) => s.setCategoriesOpen);
  const cartCount = useCartCount();
  useSearchShortcut(openSearch);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const { isMobileShell, breakpoint } = useAppShell();
  const { pathname } = useLocation();
  const home = pathname === "/";
  const collapsed = useHomeCollapsed(isMobileShell && home);

  if (isMobileShell) {
    return (
      <header
        className={cn(
          "app-chrome glass sticky top-0 z-50 border-b pt-safe-t transition-[transform,border-color,box-shadow] duration-300 ease-out",
          scrolled ? "border-[var(--border-glow)] shadow-glow-1" : "border-transparent",
        )}
      >
        <a
          href="#main"
          className="sr-only left-4 top-2 z-50 rounded-full bg-[var(--primary-strong)] px-4 py-2 text-white focus:not-sr-only focus:absolute"
        >
          Skip to content
        </a>
        {/* The chats list brings its own large-title header (search, filters). */}
        {pathname !== "/chats" && (
          <nav aria-label="Top">
            {home ? <MobileHomeBar onSearch={openSearch} collapsed={collapsed} /> : <MobileInnerBar onSearch={openSearch} />}
          </nav>
        )}
        <SearchCommand open={searchOpen} onClose={() => setSearchOpen(false)} />
        <CartDrawer />
        <CategorySheet />
      </header>
    );
  }

  if (breakpoint === "desktop") {
    return (
      <>
        <UtilityBar />
        <header
          className={cn(
            "app-chrome sticky top-0 z-50 border-b bg-surface/95 pt-safe-t backdrop-blur-xl transition-[border-color,box-shadow] duration-300",
            scrolled ? "border-line shadow-card" : "border-line",
          )}
        >
          <a
            href="#main"
            className="sr-only left-4 top-2 z-50 rounded-full bg-[var(--primary-strong)] px-4 py-2 text-white focus:not-sr-only focus:absolute"
          >
            Skip to content
          </a>
          <DesktopMainBar />
          <SearchCommand open={searchOpen} onClose={() => setSearchOpen(false)} />
          <CartDrawer />
          <CategorySheet />
        </header>
        <div className="bg-surface">
          <CategoryNavRow />
        </div>
      </>
    );
  }

  return (
    <header
      className={cn(
        "app-chrome glass sticky top-0 z-50 border-b pt-safe-t transition-[border-color,box-shadow] duration-300",
        scrolled ? "border-[var(--border-glow)] shadow-glow-1" : "border-transparent",
      )}
    >
      <a
        href="#main"
        className="sr-only left-4 top-2 z-50 rounded-full bg-[var(--primary-strong)] px-4 py-2 text-white focus:not-sr-only focus:absolute"
      >
        Skip to content
      </a>
      <nav className="container-rs flex h-14 items-center gap-1.5 sm:gap-2 md:h-16 md:gap-3" aria-label="Main">
        <Link to="/" aria-label="RealityStore home" className="mr-auto shrink-0 rounded-lg md:mr-0">
          <Logo className="hidden xs:inline-flex" />
          <Logo compact className="xs:hidden" />
        </Link>
        <MegaMenu />
        <button
          type="button"
          onClick={() => setCategoriesOpen(true)}
          className="hidden size-10 shrink-0 place-items-center rounded-full text-ink-soft hover:bg-fg/5 md:grid lg:hidden"
          aria-label="Categories"
        >
          <LayoutGrid className="size-5" aria-hidden />
        </button>
        <button
          type="button"
          onClick={openSearch}
          className="group mx-auto hidden h-10 w-full min-w-0 max-w-md items-center gap-3 rounded-full border border-line bg-fg/[0.03] px-4 text-sm text-ink-muted transition hover:border-[var(--border-glow)] hover:text-ink md:flex"
        >
          <Search className="size-4 shrink-0 text-cyan" aria-hidden />
          <span className="min-w-0 flex-1 truncate text-left">Search phones, cars, fashion…</span>
          <kbd className="hidden rounded-md border border-line px-1.5 py-0.5 font-mono text-[11px] lg:inline">⌘K</kbd>
        </button>
        <div className="flex shrink-0 items-center gap-0.5 sm:gap-1.5 md:ml-0">
          <button
            type="button"
            data-cart-target
            onClick={() => setCartOpen(true)}
            className="relative grid size-11 place-items-center rounded-full text-ink-soft transition hover:bg-fg/5 hover:text-ink md:size-10"
            aria-label={cartCount ? `Cart, ${cartCount} item${cartCount === 1 ? "" : "s"}` : "Cart"}
          >
            <ShoppingCart className="size-[22px] md:size-5" aria-hidden />
            {cartCount > 0 && (
              <span
                className="tabular absolute right-0 top-0 grid h-5 min-w-5 place-items-center rounded-full bg-[var(--primary-strong)] px-1 text-[11px] font-semibold text-white shadow-glow-1"
                aria-hidden
              >
                {cartCount > 99 ? "99+" : cartCount}
              </span>
            )}
          </button>
          {status === "authenticated" && (
            <>
              <MessagesLink />
              <NotificationBell />
            </>
          )}
          <ButtonLink to={isVendor ? "/vendor" : "/sell"} variant="ghost" size="sm" className="hidden lg:inline-flex">
            <Store className="size-4" aria-hidden /> {isVendor ? "My store" : "Sell"}
          </ButtonLink>
          {status === "loading" || status === "idle" ? (
            <Skeleton className="h-9 w-9 rounded-full md:w-24" />
          ) : status === "authenticated" ? (
            <UserMenu />
          ) : (
            <>
              <ButtonLink to="/login" variant="subtle" size="sm" className="shrink-0">
                Log in
              </ButtonLink>
              <ButtonLink to="/register" size="sm" className="hidden lg:inline-flex">
                Create account
              </ButtonLink>
            </>
          )}
        </div>
      </nav>
      <SearchCommand open={searchOpen} onClose={() => setSearchOpen(false)} />
      <CartDrawer />
      <CategorySheet />
    </header>
  );
}

/** Chat icon with the buyer-side unread count (phones use the tab bar instead). */
function MessagesLink() {
  const unread = useChatsUnread().data?.total ?? 0;
  return (
    <Link
      to="/chats"
      className="relative hidden size-10 place-items-center rounded-full text-ink-soft transition hover:bg-fg/5 hover:text-ink md:grid"
      aria-label={unread ? `Messages, ${unread} unread` : "Messages"}
    >
      <MessageSquare className="size-5" aria-hidden />
      {unread > 0 && (
        <span
          className="tabular absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-[var(--magenta-strong)] px-1 text-[11px] font-semibold text-white"
          aria-hidden
        >
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </Link>
  );
}
